import type { VisualSampleEntryBox } from '../boxes/VisualSampleEntryBox.ts'
import type { VisualSampleEntryType } from '../boxes/VisualSampleEntryType.ts'
import { UINT } from '../IsoBoxFields.ts'
import { IsoBoxWriteView } from '../IsoBoxWriteView.ts'
import type { IsoBoxWriteViewConfig } from '../IsoBoxWriteViewConfig.ts'
import { writeChildBoxes } from '../utils/writeChildBoxes.ts'

/**
 * Write a `VisualSampleEntryBox` to an `IsoBoxWriteView`.
 *
 * ISO/IEC 14496-12:2012 - 12.1.3 Visual Sample Entry
 *
 * @param box - The `VisualSampleEntryBox` fields to write
 * @param config - The configuration for the writer
 *
 * @returns An `IsoBoxWriteView` containing the encoded box
 *
 * @public
 */
export function writeVisualSampleEntryBox<T extends VisualSampleEntryType = VisualSampleEntryType>(box: VisualSampleEntryBox<T>, config: IsoBoxWriteViewConfig): IsoBoxWriteView {
	const headerSize = 8
	const reserved1Size = 6
	const dataReferenceIndexSize = 2
	const preDefined1Size = 2
	const reserved2Size = 2
	const preDefined2Size = 12 // 3 x 4 bytes
	const widthSize = 2
	const heightSize = 2
	const horizresolutionSize = 4
	const vertresolutionSize = 4
	const reserved3Size = 4
	const frameCountSize = 2
	const compressorNameSize = 32
	const depthSize = 2
	const preDefined3Size = 2
	const { bytes, size } = writeChildBoxes(box.boxes, config)
	const totalSize = headerSize + reserved1Size + dataReferenceIndexSize + preDefined1Size +
		reserved2Size + preDefined2Size + widthSize + heightSize + horizresolutionSize +
		vertresolutionSize + reserved3Size + frameCountSize + compressorNameSize +
		depthSize + preDefined3Size + size

	const writer = new IsoBoxWriteView(box.type, totalSize)

	writer.writeArray(box.reserved1, UINT, 1, 6)
	writer.writeUint(box.dataReferenceIndex, 2)
	writer.writeUint(box.preDefined1, 2)
	writer.writeUint(box.reserved2, 2)
	writer.writeArray(box.preDefined2, UINT, 4, 3)
	writer.writeUint(box.width, 2)
	writer.writeUint(box.height, 2)
	writer.writeTemplate(box.horizresolution, 4)
	writer.writeTemplate(box.vertresolution, 4)
	writer.writeUint(box.reserved3, 4)
	writer.writeUint(box.frameCount, 2)
	writer.writeArray(box.compressorName, UINT, 1, 32)
	writer.writeUint(box.depth, 2)
	writer.writeUint(box.preDefined3 & 0xFFFF, 2)
	writer.writeBytes(bytes)

	return writer
}
